import React, { useState } from 'react';
import { collection, query, where,getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import './RecalculateStudentBalances.css';

/**
 * ADMIN-ONLY Utility to Recalculate Student Balances Across ALL Franchisees
 * 
 * This fixes students with missing/incorrect payment fields.
 * SAFE: Only touches students that have actual issues.
 */

function AdminRecalculateStudentBalances({ onClose }) {
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [affectedStudents, setAffectedStudents] = useState([]);
  const [franchiseeSummary, setFranchiseeSummary] = useState({});
  const [results, setResults] = useState(null);

  // Calculate which terms a student should be charged for
  const calculateTermsOwed = (studentStartDate, termStructure, studentName = 'Unknown') => {
    if (!termStructure || !termStructure.termDates) {
      console.log(`[${studentName}] âŒ No term structure or termDates`);
      return [];
    }
    
    const startDate = studentStartDate.toDate ? studentStartDate.toDate() : new Date(studentStartDate);
    startDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const currentYear = new Date().getFullYear();
    const termsOwed = [];
    
    console.log(`[${studentName}] Calculating terms...`);
    console.log(`  Student start date: ${startDate.toLocaleDateString()}`);
    console.log(`  Today: ${today.toLocaleDateString()}`);
    
    for (let termNum = 1; termNum <= 4; termNum++) {
      const termData = termStructure.termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) {
        console.log(`  Term ${termNum}: âš ï¸ No term data`);
        continue;
      }
      
      try {
        const [startMonth, startDay] = termData.start.split('-').map(Number);
        const [endMonth, endDay] = termData.end.split('-').map(Number);
        
        console.log(`  Term ${termNum}: ${termData.start} to ${termData.end}`);
        console.log(`    Parsed: month=${startMonth}, day=${startDay}`);
        
        // Validate month/day ranges
        if (startMonth < 1 || startMonth > 12 || endMonth < 1 || endMonth > 12) {
          console.warn(`  Term ${termNum}: âŒ Invalid month: ${termData.start} - ${termData.end}`);
          continue;
        }
        
        const termStart = new Date(currentYear, startMonth - 1, startDay);
        termStart.setHours(0, 0, 0, 0);
        
        const termEnd = new Date(currentYear, endMonth - 1, endDay);
        termEnd.setHours(23, 59, 59, 999);
        
        console.log(`    Term start: ${termStart.toLocaleDateString()}`);
        console.log(`    Term end: ${termEnd.toLocaleDateString()}`);
        
        const enrolledBeforeTermEnded = startDate <= termEnd;
        const termHasStarted = today >= termStart;
        
        console.log(`    Student enrolled before term ended? ${enrolledBeforeTermEnded}`);
        console.log(`    Term has started? ${termHasStarted}`);
        
        // CRITICAL FIX: Only charge if term has STARTED (today >= termStart)
        // AND student was enrolled before term ended
        if (enrolledBeforeTermEnded && termHasStarted) {
          console.log(`    âœ… CHARGING for Term ${termNum}`);
          termsOwed.push(termNum);
        } else {
          console.log(`    âŒ NOT charging for Term ${termNum}`);
        }
      } catch (error) {
        console.error(`  Term ${termNum}: âŒ Error parsing dates:`, error);
      }
    }
    
    console.log(`[${studentName}] Terms owed: [${termsOwed.join(', ')}]`);
    return termsOwed;
  };

  // Calculate correct balance for a student
  const calculateCorrectBalance = async (student) => {
    try {
      console.log(`\n=== Calculating for: ${student.studentName} ===`);
      
      // Get school data
      if (!student.schoolRef) {
        console.log(`âŒ No school reference`);
        return { error: 'No school reference' };
      }
      
      const schoolDoc = await getDoc(student.schoolRef);
      if (!schoolDoc.exists()) {
        console.log(`âŒ School not found`);
        return { error: 'School not found' };
      }
      
      const schoolData = schoolDoc.data();
      console.log(`School: ${schoolData.schoolName || 'Unknown'}`);
      
      // Get term structure
      if (!schoolData.termStructureRef) {
        console.log(`âŒ No term structure reference`);
        return { error: 'No term structure' };
      }
      
      const termStructureDoc = await getDoc(schoolData.termStructureRef);
      if (!termStructureDoc.exists()) {
        console.log(`âŒ Term structure not found`);
        return { error: 'Term structure not found' };
      }
      
      const termStructure = termStructureDoc.data();
      console.log(`Term structure found`);
      
      // Calculate terms owed (with debug logging)
      const termsOwed = calculateTermsOwed(student.startDate, termStructure, student.studentName);
      
      // Calculate expected fees
      const adminFee = student.adminFee || 0;
      const termFee = student.feeStructure?.standardFee || 0;
      const totalTermFees = termsOwed.length * termFee;
      const correctTotalExpected = adminFee + totalTermFees;
      
      console.log(`Admin fee: R${adminFee}`);
      console.log(`Term fee: R${termFee}`);
      console.log(`Total term fees: R${totalTermFees} (${termsOwed.length} terms Ã— R${termFee})`);
      console.log(`Correct total expected: R${correctTotalExpected}`);
      
      // Use the stored totalPaid field (includes all payments + creditBalance)
      // CRITICAL: Don't recalculate from termPayments as it excludes creditBalance
      const totalPaid = student.totalPaid || 0;
      
      console.log(`Total paid: R${totalPaid}`);
      
      // Calculate correct balance (negative = owing)
      const correctBalance = -(correctTotalExpected - totalPaid);
      
      console.log(`Correct balance: R${correctBalance}`);
      console.log(`===========================\n`);
      
      return {
        correctTotalExpected,
        correctBalance,
        totalPaid,
        termsOwed,
        termFee,
        adminFee
      };
    } catch (error) {
      console.error('Error calculating balance:', error);
      return { error: error.message };
    }
  };

  // Helper function to add delay between operations
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Scan ALL students across ALL franchisees
  const scanAllStudents = async () => {
    setScanning(true);
    setAffectedStudents([]);
    setFranchiseeSummary({});
    
    try {
      // Load ALL franchisees
      const franchiseesSnapshot = await getDocs(collection(db, 'Franchisees'));
      const totalFranchisees = franchiseesSnapshot.size;
      
      console.log(`Scanning ${totalFranchisees} franchisees...`);
      
      const affected = [];
      const summary = {};
      
      let franchiseeCount = 0;
      
      // Scan each franchisee's students with delays
      for (const franchiseeDoc of franchiseesSnapshot.docs) {
        franchiseeCount++;
        
        // Add delay every 3 franchisees to avoid overwhelming Firestore
        if (franchiseeCount > 1 && franchiseeCount % 3 === 0) {
          console.log(`⏸️ Pausing briefly (processed ${franchiseeCount}/${totalFranchisees})...`);
          await delay(2000); // 2 second delay every 3 franchisees
        }
        const franchiseeId = franchiseeDoc.id;
        const franchiseeData = franchiseeDoc.data();
        const franchiseeName = franchiseeData.name || franchiseeData.email || franchiseeId;
        
        console.log(`Scanning franchisee: ${franchiseeName}`);
        
        const franchiseeRef = doc(db, 'Franchisees', franchiseeId);
        
        // Load this franchisee's students
        const studentsSnapshot = await getDocs(
          query(
            collection(db, 'students'),
            where('franchiseeRef', '==', franchiseeRef)
          )
        );
        
        let franchiseeAffectedCount = 0;
        
        for (const studentDoc of studentsSnapshot.docs) {
          const student = { id: studentDoc.id, ...studentDoc.data() };
          
          // Calculate what the balance SHOULD be
          const calculation = await calculateCorrectBalance(student);
          
          if (calculation.error) {
            // Skip students with errors (no school, no term structure, etc.)
            continue;
          }
          
          const currentTotalExpected = student.totalExpected || 0;
          const currentBalance = student.balance || 0;
          
          // Check if values are wrong
          const hasWrongValues = 
            Math.abs(currentTotalExpected - calculation.correctTotalExpected) > 0.01 ||
            Math.abs(currentBalance - calculation.correctBalance) > 0.01 ||
            student.balance === undefined ||
            student.totalExpected === undefined ||
            !student.invoices ||
            !student.paymentHistory ||
            student.creditBalance === undefined ||
            student.lastPaymentDate === undefined ||
            typeof student.adminFeePaid === 'boolean' ||
            // CRITICAL: Detect students with creditBalance that should be in termPayments
            // If balance <= 0 (fully paid or owing) but creditBalance > 0, payments are misallocated
            (student.creditBalance > 0 && student.balance <= 0);
          
          if (hasWrongValues) {
            franchiseeAffectedCount++;
            
            affected.push({
              ...student,
              calculation,
              currentTotalExpected,
              currentBalance,
              franchiseeId,
              franchiseeName,
              issues: []
                .concat(student.balance === undefined ? ['âŒ Missing balance field'] : [])
                .concat(student.totalExpected === undefined ? ['âŒ Missing totalExpected field'] : [])
                .concat(!student.invoices ? ['âŒ Missing invoices field'] : [])
                .concat(!student.paymentHistory ? ['âŒ Missing paymentHistory field'] : [])
                .concat(student.creditBalance === undefined ? ['âŒ Missing creditBalance field'] : [])
                .concat(student.lastPaymentDate === undefined ? ['âŒ Missing lastPaymentDate field'] : [])
                .concat(typeof student.adminFeePaid === 'boolean' ? ['âŒ Wrong adminFeePaid type (boolean vs number)'] : [])
                .concat(Math.abs(currentTotalExpected - calculation.correctTotalExpected) > 0.01 ? 
                  [`âš ï¸ Wrong totalExpected: ${currentTotalExpected} should be ${calculation.correctTotalExpected}`] : [])
                .concat(Math.abs(currentBalance - calculation.correctBalance) > 0.01 ? 
                  [`âš ï¸ Wrong balance: ${currentBalance} should be ${calculation.correctBalance}`] : [])
                .concat((student.creditBalance > 0 && student.balance <= 0) ?
                  [`💰 Misallocated creditBalance: R${student.creditBalance} should be in termPayments`] : [])
            });
          }
        }
        
        if (franchiseeAffectedCount > 0) {
          summary[franchiseeId] = {
            name: franchiseeName,
            count: franchiseeAffectedCount
          };
        }
      }
      
      setAffectedStudents(affected);
      setFranchiseeSummary(summary);
      
      alert(
        `âœ… Scan complete!\n\n` +
        `Scanned: ${totalFranchisees} franchisees\n` +
        `Affected franchisees: ${Object.keys(summary).length}\n` +
        `Affected students: ${affected.length}\n\n` +
        `Students with all correct fields were NOT touched.`
      );
    } catch (error) {
      console.error('Error scanning:', error);
      alert('âŒ Error scanning students: ' + error.message);
    } finally {
      setScanning(false);
    }
  };

  // Fix all affected students
  const fixAllStudents = async () => {
    if (affectedStudents.length === 0) {
      alert('No students to fix. Run scan first.');
      return;
    }
    
    if (!window.confirm(
      `âš ï¸ ADMIN FIX - ALL FRANCHISEES\n\n` +
      `This will fix ${affectedStudents.length} students across ${Object.keys(franchiseeSummary).length} franchisees.\n\n` +
      `Updates include:\n` +
      `- totalExpected\n` +
      `- balance\n` +
      `- totalOwing\n` +
      `- adminFeePaid (if wrong type)\n` +
      `- invoices (if missing)\n` +
      `- paymentHistory (if missing)\n` +
      `- creditBalance (if missing)\n` +
      `- lastPaymentDate (if missing)\n\n` +
      `âœ… Students with correct fields will NOT be touched.\n\n` +
      `This cannot be undone. Continue?`
    )) {
      return;
    }
    
    setFixing(true);
    let successCount = 0;
    let errorCount = 0;
    const franchiseeResults = {};
    
    try {
      for (const student of affectedStudents) {
        try {
          const studentRef = doc(db, 'students', student.id);
          
          // Build update object with all necessary fields
          const updateData = {
            totalExpected: student.calculation.correctTotalExpected,
            balance: student.calculation.correctBalance,
            totalOwing: Math.abs(Math.min(student.calculation.correctBalance, 0)),
            updatedAt: new Date()
          };
          
          // CRITICAL FIX: Reallocate payments if creditBalance exists
          if (student.creditBalance && student.creditBalance > 0) {
            console.log(`\n💰 Reallocating R${student.creditBalance} creditBalance for ${student.studentName}`);
            
            const termFee = student.calculation.termFee || student.feeStructure?.standardFee || 0;
            const termsOwed = student.calculation.termsOwed || [];
            let remainingCredit = student.creditBalance;
            
            console.log(`  Term fee: R${termFee}`);
            console.log(`  Terms owed: ${termsOwed.join(', ')}`);
            
            // Allocate admin fee if not fully paid
            const adminFee = student.calculation.adminFee || student.adminFee || 0;
            const adminPaid = student.termPayments?.admin || 0;
            
            if (adminPaid < adminFee && remainingCredit > 0) {
              const adminNeeded = adminFee - adminPaid;
              const adminAllocation = Math.min(remainingCredit, adminNeeded);
              updateData['termPayments.admin'] = adminPaid + adminAllocation;
              remainingCredit -= adminAllocation;
              console.log(`  ✓ Allocated R${adminAllocation} to admin (now R${adminPaid + adminAllocation})`);
            }
            
            // Allocate to terms in order
            for (const termNum of termsOwed) {
              if (remainingCredit <= 0) break;
              
              const termKey = `term${termNum}`;
              const termPaid = student.termPayments?.[termKey] || 0;
              const termNeeded = termFee - termPaid;
              
              if (termNeeded > 0) {
                const termAllocation = Math.min(remainingCredit, termNeeded);
                updateData[`termPayments.${termKey}`] = termPaid + termAllocation;
                remainingCredit -= termAllocation;
                console.log(`  ✓ Allocated R${termAllocation} to term${termNum} (now R${termPaid + termAllocation})`);
              }
            }
            
            // Update creditBalance to remaining (should be 0 if all allocated)
            updateData.creditBalance = remainingCredit;
            console.log(`  ✓ New creditBalance: R${remainingCredit}`);
          }
          
          // Fix adminFeePaid if it's boolean instead of number
          if (typeof student.adminFeePaid === 'boolean') {
            updateData.adminFeePaid = 0;
          }
          
          // Add missing invoices field
          if (!student.invoices) {
            updateData.invoices = {
              term1: null,
              term2: null,
              term3: null,
              term4: null
            };
          }
          
          // Add missing paymentHistory field
          if (!student.paymentHistory) {
            updateData.paymentHistory = [];
          }
          
          // Add missing creditBalance field
          if (student.creditBalance === undefined) {
            updateData.creditBalance = 0;
          }
          
          // Add missing lastPaymentDate field
          if (student.lastPaymentDate === undefined) {
            updateData.lastPaymentDate = null;
          }
          
          // Add missing brand building fields
          if (student.bbContributionDue === undefined) {
            const bbAmount = student.adminFeeBreakdown?.bbContribution || 0;
            updateData.bbContributionDue = bbAmount;
          }
          
          if (student.bbContributionPaid === undefined) {
            updateData.bbContributionPaid = false;
          }
          
          if (student.bbContributionDate === undefined) {
            updateData.bbContributionDate = null;
          }
          
          await updateDoc(studentRef, updateData);
          
          successCount++;
          
          // Track by franchisee
          if (!franchiseeResults[student.franchiseeId]) {
            franchiseeResults[student.franchiseeId] = {
              name: student.franchiseeName,
              success: 0,
              errors: 0
            };
          }
          franchiseeResults[student.franchiseeId].success++;
          
        } catch (error) {
          console.error(`Error fixing ${student.studentName}:`, error);
          errorCount++;
          
          if (!franchiseeResults[student.franchiseeId]) {
            franchiseeResults[student.franchiseeId] = {
              name: student.franchiseeName,
              success: 0,
              errors: 0
            };
          }
          franchiseeResults[student.franchiseeId].errors++;
        }
      }
      
      setResults({
        success: successCount,
        errors: errorCount,
        total: affectedStudents.length,
        byFranchisee: franchiseeResults
      });
      
      // Build result message
      let resultMessage = `âœ… Fix complete!\n\n`;
      resultMessage += `Successfully fixed: ${successCount}\n`;
      resultMessage += `Errors: ${errorCount}\n`;
      resultMessage += `Total: ${affectedStudents.length}\n\n`;
      resultMessage += `BY FRANCHISEE:\n`;
      
      Object.entries(franchiseeResults).forEach(([id, data]) => {
        resultMessage += `\n${data.name}:\n`;
        resultMessage += `  âœ… Fixed: ${data.success}\n`;
        if (data.errors > 0) {
          resultMessage += `  âŒ Errors: ${data.errors}\n`;
        }
      });
      
      alert(resultMessage);
      
      // Clear affected students list
      setAffectedStudents([]);
      setFranchiseeSummary({});
    } catch (error) {
      console.error('Error fixing students:', error);
      alert('âŒ Error fixing students: ' + error.message);
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="recalculate-container">
      <div className="recalculate-header">
        <button onClick={onClose} className="back-button">
          â† Back to Admin Dashboard
        </button>
        <h1>ðŸ”§ Admin: Recalculate Student Balances</h1>
        <p className="header-subtitle">
          Fix students across ALL franchisees with missing/incorrect payment fields
        </p>
      </div>

      <div className="recalculate-content">
        <div className="info-box">
          <h3>âš ï¸ ADMIN-ONLY TOOL</h3>
          <p style={{color: 'var(--babs-red)', fontWeight: 'bold', marginBottom: '15px'}}>
            This tool scans and fixes students across ALL franchisees in the system.
          </p>
          
          <h3>What This Tool Does:</h3>
          <ul>
            <li>Scans ALL students across ALL franchisees</li>
            <li>Identifies students with incorrect or missing payment fields</li>
            <li>Recalculates correct values based on their school's term structure</li>
            <li><strong>Only charges for terms that have STARTED</strong> (not future terms)</li>
            <li>Updates only affected students (ignores students with correct fields)</li>
            <li>Shows results grouped by franchisee</li>
          </ul>
          
          <h3>âœ… Charging Logic:</h3>
          <ul>
            <li><strong>Today: {new Date().toLocaleDateString('en-ZA')}</strong></li>
            <li>Only charges for terms where <strong>term start date â‰¤ today</strong></li>
            <li>Example: If only Term 1 has started, only charges admin + 1 term fee</li>
            <li>Does NOT charge for future terms that haven't started yet</li>
          </ul>
          
          <h3>âœ… Safety Guarantees:</h3>
          <ul>
            <li><strong>Only touches broken students</strong> - Students with all correct fields are ignored</li>
            <li><strong>No deletions</strong> - Only adds missing fields or corrects wrong values</li>
            <li><strong>Preview before fix</strong> - See exactly what will be changed</li>
            <li><strong>Detailed logging</strong> - Shows which franchisees are affected</li>
          </ul>
          
          <h3>Fields Fixed/Added:</h3>
          <ul>
            <li><strong>balance</strong> - Main balance field (negative = owing)</li>
            <li><strong>totalExpected</strong> - Total fees expected from student</li>
            <li><strong>totalOwing</strong> - Absolute value of amount owing</li>
            <li><strong>invoices</strong> - Invoice tracking object (term1-4)</li>
            <li><strong>paymentHistory</strong> - Payment history array</li>
            <li><strong>creditBalance</strong> - Credit/overpayment tracking</li>
            <li><strong>lastPaymentDate</strong> - Last payment timestamp</li>
            <li><strong>adminFeePaid</strong> - Fix if wrong type (boolean â†’ number)</li>
            <li><strong>bbContribution fields</strong> - Brand building tracking</li>
          </ul>
        </div>

        <div className="action-buttons">
          <button
            onClick={scanAllStudents}
            disabled={scanning || fixing}
            className="btn-scan"
          >
            {scanning ? 'ðŸ” Scanning All Franchisees...' : 'ðŸ” Scan ALL Franchisees'}
          </button>

          {affectedStudents.length > 0 && (
            <button
              onClick={fixAllStudents}
              disabled={scanning || fixing}
              className="btn-fix"
            >
              {fixing ? 'âš™ï¸ Fixing...' : `âš™ï¸ Fix ${affectedStudents.length} Students`}
            </button>
          )}
        </div>

        {Object.keys(franchiseeSummary).length > 0 && (
          <div className="franchisee-summary">
            <h3>ðŸ“Š Affected Franchisees ({Object.keys(franchiseeSummary).length})</h3>
            <div className="summary-grid">
              {Object.entries(franchiseeSummary).map(([id, data]) => (
                <div key={id} className="summary-card">
                  <div className="summary-name">{data.name}</div>
                  <div className="summary-count">{data.count} students need fixing</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {affectedStudents.length > 0 && (
          <div className="affected-students-list">
            <h3>ðŸ“‹ Affected Students ({affectedStudents.length})</h3>
            <table>
              <thead>
                <tr>
                  <th>Franchisee</th>
                  <th>Student Name</th>
                  <th>School</th>
                  <th>Current Total Expected</th>
                  <th>Correct Total Expected</th>
                  <th>Current Balance</th>
                  <th>Correct Balance</th>
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                {affectedStudents.map(student => (
                  <tr key={student.id}>
                    <td><strong>{student.franchiseeName}</strong></td>
                    <td>{student.studentName}</td>
                    <td>{student.schoolName}</td>
                    <td className={student.totalExpected === undefined ? 'missing' : 'wrong'}>
                      {student.totalExpected === undefined ? 'MISSING' : `R${student.currentTotalExpected.toFixed(2)}`}
                    </td>
                    <td className="correct">R{student.calculation.correctTotalExpected.toFixed(2)}</td>
                    <td className={student.balance === undefined ? 'missing' : 'wrong'}>
                      {student.balance === undefined ? 'MISSING' : `R${student.currentBalance.toFixed(2)}`}
                    </td>
                    <td className="correct">R{student.calculation.correctBalance.toFixed(2)}</td>
                    <td className="issues">
                      {student.issues.map((issue, i) => (
                        <div key={i}>{issue}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {results && (
          <div className="results-box">
            <h3>âœ… Results</h3>
            <p><strong>Successfully Fixed:</strong> {results.success}</p>
            <p><strong>Errors:</strong> {results.errors}</p>
            <p><strong>Total Processed:</strong> {results.total}</p>
            
            <h4 style={{marginTop: '20px'}}>By Franchisee:</h4>
            {Object.entries(results.byFranchisee).map(([id, data]) => (
              <div key={id} style={{marginLeft: '20px', marginTop: '10px'}}>
                <strong>{data.name}:</strong>
                <div style={{marginLeft: '20px'}}>
                  âœ… Fixed: {data.success}
                  {data.errors > 0 && <><br/>âŒ Errors: {data.errors}</>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminRecalculateStudentBalances;